import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Chip, Drawer, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Input from '@/components/Common/Input';
import Button from '@/components/Common/Button';
import { useGetAgent, useUpdateAgent } from '@/apis/business/agent/hook';
import type { AgentResponse } from '@/apis/business/agent/types';
import { useGenerateSentimentKeywords, useGetDefaultSentimentKeywords } from '@/apis/business/agent/signals/hook';
import type { SignalKeywordItem } from '@/apis/business/agent/signals/types';
import { KeywordSettingsFormData } from './types';
import { IoSparklesOutline } from 'react-icons/io5';
import { MdCheck, MdClose, MdOutlineModeEdit } from 'react-icons/md';
import { titleCase } from 'utils/helperFunctions';
import { KeywordSystemTagsSkeleton } from './SignalsSkeletons';

function keywordLabel(item: SignalKeywordItem) {
  return titleCase(item.keyword);
}

type EditingSource = 'suggested' | 'selected';

interface EditingContext {
  source: EditingSource;
  originalKeyword: string;
}

interface KeywordSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function KeywordSettingsDrawer({ open, onClose }: KeywordSettingsDrawerProps) {
  const theme = useTheme();
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();
  const queryClient = useQueryClient();
  const { data: agent, refetch: refetchAgent } = useGetAgent();
  const { data: systemKeywordsResult, isLoading: isSystemKeywordsLoading } = useGetDefaultSentimentKeywords(open);
  const { mutate: generateKeywordsMutation, isPending: isGeneratePending } = useGenerateSentimentKeywords();
  const { mutate: updateAgent, isPending: isUpdatePending } = useUpdateAgent();

  const { control, handleSubmit, reset, getValues } = useForm<KeywordSettingsFormData>({
    defaultValues: { customPrompt: '' }
  });

  const [suggestedTags, setSuggestedTags] = useState<SignalKeywordItem[]>([]);
  const [editingContext, setEditingContext] = useState<EditingContext | null>(null);
  const [draftKeyword, setDraftKeyword] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameBeforeEdit, setNameBeforeEdit] = useState('');
  const [duplicateError, setDuplicateError] = useState('');

  const systemKeywords = systemKeywordsResult?.data ?? [];
  const customKeywords = agent?.custom_keywords ?? [];

  const invalidateSignals = () => {
    queryClient.invalidateQueries({ queryKey: ['signals', locationId, agentId] });
  };

  const getAgentSnapshot = () => queryClient.getQueryData<AgentResponse>(['agent', locationId, agentId]) ?? agent;

  const closeEditor = () => {
    setEditingContext(null);
    setDraftKeyword('');
    setDraftDescription('');
    setIsEditingName(false);
    setNameBeforeEdit('');
    setDuplicateError('');
  };

  const openEditor = (item: SignalKeywordItem, source: EditingSource) => {
    if (isUpdatePending) return;
    setEditingContext({ source, originalKeyword: item.keyword });
    setDraftKeyword(item.keyword);
    setDraftDescription(item.description ?? '');
    setIsEditingName(false);
    setNameBeforeEdit('');
    setDuplicateError('');
  };

  const isDuplicateKeyword = (keyword: string, excludeOriginal?: string) => {
    const normalized = keyword.trim().toLowerCase();
    const current = getAgentSnapshot()?.custom_keywords ?? [];
    return current.some((k) => {
      if (excludeOriginal && k.keyword.toLowerCase() === excludeOriginal.toLowerCase()) {
        return false;
      }
      return k.keyword.toLowerCase() === normalized;
    });
  };

  useEffect(() => {
    if (open) {
      refetchAgent();
    }
  }, [open, refetchAgent]);

  useEffect(() => {
    if (!open) {
      reset({ customPrompt: '' });
      setSuggestedTags([]);
      setEditingContext(null);
      setDraftKeyword('');
      setDraftDescription('');
      setIsEditingName(false);
      setNameBeforeEdit('');
      setDuplicateError('');
    }
  }, [open, reset]);

  const onSubmit = () => {
    reset({ customPrompt: '' });
    setSuggestedTags([]);
    closeEditor();
    onClose();
  };

  const handleGenerate = () => {
    const prompt = getValues('customPrompt').trim();
    if (!prompt) return;

    generateKeywordsMutation(prompt, {
      onSuccess: (res) => {
        closeEditor();
        const rows = res?.data ?? [];
        const existingKeys = new Set((getAgentSnapshot()?.custom_keywords ?? []).map((k) => k.keyword.toLowerCase()));
        setSuggestedTags(rows.filter((row) => !existingKeys.has(row.keyword.toLowerCase())));
      }
    });
  };

  const handleConfirmKeyword = () => {
    const keyword = draftKeyword.trim();
    const description = draftDescription.trim();

    if (!keyword || !editingContext || isUpdatePending) return;

    const excludeOriginal = editingContext.source === 'selected' ? editingContext.originalKeyword : undefined;
    if (isDuplicateKeyword(keyword, excludeOriginal)) {
      setDuplicateError('A keyword with this name already exists.');
      return;
    }

    const updatedItem: SignalKeywordItem = { keyword, description };
    const current = getAgentSnapshot()?.custom_keywords ?? [];

    if (editingContext.source === 'suggested') {
      updateAgent(
        { agent: { custom_keywords: [...current, updatedItem] } },
        {
          onSuccess: () => {
            invalidateSignals();
            setSuggestedTags((prev) => prev.filter((k) => k.keyword !== editingContext.originalKeyword));
            closeEditor();
          }
        }
      );
      return;
    }

    updateAgent(
      {
        agent: {
          custom_keywords: current.map((k) => (k.keyword === editingContext.originalKeyword ? updatedItem : k))
        }
      },
      {
        onSuccess: () => {
          invalidateSignals();
          closeEditor();
        }
      }
    );
  };

  const removeCustomKeyword = (keyword: string) => {
    if (isUpdatePending) return;
    if (editingContext?.source === 'selected' && editingContext.originalKeyword === keyword) {
      closeEditor();
    }
    const current = getAgentSnapshot()?.custom_keywords ?? [];
    updateAgent(
      { agent: { custom_keywords: current.filter((k) => k.keyword !== keyword) } },
      { onSuccess: () => invalidateSignals() }
    );
  };

  const handleStartEditName = () => {
    setNameBeforeEdit(draftKeyword);
    setIsEditingName(true);
  };

  const handleConfirmEditName = () => {
    setDraftKeyword(draftKeyword.trim());
    setIsEditingName(false);
    setDuplicateError('');
  };

  const handleCancelEditName = () => {
    setDraftKeyword(nameBeforeEdit);
    setIsEditingName(false);
  };

  const isConfirmDisabled = !draftKeyword.trim() || isUpdatePending;

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 430 },
          borderRadius: 0
        }
      }}
    >
      <Box component='form' onSubmit={handleSubmit(onSubmit)} display='flex' flexDirection='column' height='100%'>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          p={3}
          borderBottom='1px solid #ECECEC'
        >
          <Typography fontSize={18} fontWeight={600}>
            Keyword Settings
          </Typography>
          <IconButton onClick={onClose}>
            <Typography fontSize={24} lineHeight={1}>
              ×
            </Typography>
          </IconButton>
        </Stack>

        <Box p={3} flex={1} overflow='auto'>
          <Stack direction='row' alignItems='center' gap={1} mb={1}>
            <Typography fontWeight={500}>Default Keywords</Typography>
            <Chip label='System' size='small' sx={{ borderColor: '#ECECEC', borderRadius: 48, bgcolor: '#F8F9FA' }} />
            <Typography fontSize={12} color='text.secondary' ml='auto'>
              Auto-tracked · Read only
            </Typography>
          </Stack>
          <Box
            border='1px solid #ECECEC'
            borderRadius={2}
            p={1.5}
            display='flex'
            flexWrap='wrap'
            gap={1}
            mb={3}
            minHeight={48}
            alignItems='center'
          >
            {isSystemKeywordsLoading ? (
              <KeywordSystemTagsSkeleton />
            ) : (
              systemKeywords.map((item) => (
                <Tooltip key={item.keyword} title={item.description || ''} placement='top' arrow>
                  <Chip
                    label={keywordLabel(item)}
                    variant='outlined'
                    sx={{ borderColor: '#ECECEC', borderRadius: 48, bgcolor: '#F8F9FA' }}
                  />
                </Tooltip>
              ))
            )}
          </Box>

          <Stack direction='row' alignItems='center' gap={1} mb={1}>
            <Typography fontWeight={500}>Business-Specific Keywords</Typography>
            <Chip
              label='Custom'
              size='small'
              sx={{ borderColor: 'primary.main', borderRadius: 48, bgcolor: 'primary.light', color: 'primary.main' }}
            />
          </Stack>
          <Typography color='text.secondary' fontSize={14} mb={1.5}>
            Tell us what you want to track in simple words, we&apos;ll create keywords for it.
          </Typography>
          <Input name='customPrompt' control={control} placeholder='Enter Here' rows={3} fullWidth multiline />
          <Box mt={1.5} display='flex' justifyContent='flex-end'>
            <Button
              type='button'
              variant='contained'
              onClick={handleGenerate}
              isPending={isGeneratePending}
              disabled={isUpdatePending}
              sx={{ borderRadius: '999px', px: 3, py: 0.9, minWidth: 140 }}
            >
              <Box display='flex' alignItems='center' gap={0.8}>
                <IoSparklesOutline />
                Generate
              </Box>
            </Button>
          </Box>

          {suggestedTags.length > 0 && (
            <Box mt={2}>
              <Typography color='text.secondary' fontSize={14} mb={1}>
                Select Tags to Add:
              </Typography>
              <Box display='flex' flexWrap='wrap' gap={1}>
                {suggestedTags.map((item) => {
                  const isActive =
                    editingContext?.source === 'suggested' && editingContext.originalKeyword === item.keyword;

                  return (
                    <Tooltip key={item.keyword} title={item.description || ''} placement='top' arrow>
                      <Chip
                        label={`+ ${keywordLabel(item)}`}
                        variant='outlined'
                        onClick={() => openEditor(item, 'suggested')}
                        sx={{
                          borderColor: isActive ? 'primary.main' : '#ECECEC',
                          borderRadius: 48,
                          bgcolor: isActive ? 'primary.light' : '#fff',
                          color: isActive ? 'primary.main' : 'text.primary',
                          cursor: isUpdatePending ? 'default' : 'pointer',
                          opacity: isUpdatePending ? 0.6 : 1,
                          fontWeight: 500,
                          '&:hover': { bgcolor: isUpdatePending ? '#fff' : isActive ? 'primary.light' : '#F8F9FA' }
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          )}

          {editingContext && (
            <Box
              mt={2}
              p={2}
              sx={{
                bgcolor: '#f6f6f6',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2
              }}
            >
              <Stack direction='row' alignItems='center' gap={0.5} mb={2}>
                {isEditingName ? (
                  <>
                    <TextField
                      size='small'
                      value={draftKeyword}
                      onChange={(e) => {
                        setDraftKeyword(e.target.value);
                        setDuplicateError('');
                      }}
                      autoFocus
                      disabled={isUpdatePending}
                      sx={{
                        minWidth: 160,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#fff'
                        }
                      }}
                    />
                    <IconButton size='small' onClick={handleConfirmEditName} color='primary' disabled={isUpdatePending}>
                      <MdCheck size={18} />
                    </IconButton>
                    <IconButton size='small' onClick={handleCancelEditName} disabled={isUpdatePending}>
                      <MdClose size={18} />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <Typography fontWeight={500} fontSize={15}>
                      {titleCase(draftKeyword)}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={handleStartEditName}
                      disabled={isUpdatePending}
                      sx={{ color: theme.palette.primary.main }}
                    >
                      <MdOutlineModeEdit size={16} />
                    </IconButton>
                  </>
                )}
              </Stack>

              <TextField
                fullWidth
                multiline
                minRows={3}
                value={draftDescription}
                onChange={(e) => setDraftDescription(e.target.value)}
                placeholder='Describe what this keyword tracks…'
                disabled={isUpdatePending}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff'
                  }
                }}
              />

              {duplicateError && (
                <Typography variant='body2' color='error' mb={1.5}>
                  {duplicateError}
                </Typography>
              )}

              <Stack direction='row' justifyContent='flex-end' gap={1}>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={closeEditor}
                  disabled={isUpdatePending}
                  sx={{ minHeight: 20, fontSize: 13 }}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  onClick={handleConfirmKeyword}
                  size='small'
                  disabled={isConfirmDisabled}
                  sx={{ minHeight: 20, fontSize: 13 }}
                  isPending={isUpdatePending}
                >
                  Add Keyword
                </Button>
              </Stack>
            </Box>
          )}

          {customKeywords.length > 0 && (
            <Stack direction='row' flexWrap='wrap' gap={1} mt={2}>
              {customKeywords.map((item) => {
                const isActive =
                  editingContext?.source === 'selected' && editingContext.originalKeyword === item.keyword;

                return (
                  <Tooltip key={item.keyword} placement='top' title={item.description || ''} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        pl: 1.5,
                        pr: 0.5,
                        py: 0.25,
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: '48px',
                        bgcolor: isActive ? theme.palette.primary.main : theme.palette.primary.light,
                        color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main,
                        opacity: isUpdatePending ? 0.6 : 1
                      }}
                    >
                      <Typography variant='body2' fontWeight={500} fontSize={13}>
                        {keywordLabel(item)}
                      </Typography>
                      <IconButton
                        size='small'
                        onClick={() => openEditor(item, 'selected')}
                        disabled={isUpdatePending}
                        sx={{
                          color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main,
                          p: 0.25
                        }}
                      >
                        <MdOutlineModeEdit size={14} />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => removeCustomKeyword(item.keyword)}
                        disabled={isUpdatePending}
                        sx={{
                          color: isActive ? theme.palette.primary.contrastText : theme.palette.primary.main,
                          p: 0.25
                        }}
                      >
                        <MdClose size={14} />
                      </IconButton>
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          )}
        </Box>

        <Stack direction='row' justifyContent='flex-end' gap={1.5} p={2.5} borderTop='1px solid #ECECEC'>
          <Button
            variant='outlined'
            color='inherit'
            onClick={onClose}
            sx={{ borderRadius: '999px', px: 3, borderColor: '#888888', color: '#888888' }}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isUpdatePending} sx={{ borderRadius: '999px', px: 3 }}>
            Save
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
